import React, { useState } from 'react';
import { OfferFeeSummary } from './OfferFeeSummary';
import { OfferModal, PaymentOptionList, type PaymentAmountOption } from './OfferModal';
import { formatMoney } from './offerFees';
import { MIN_OFFER_PAYMENT_AMOUNT } from './paymentLinks';

interface RequestPaymentModalProps {
  currency: string;
  payerName: string;
  options: PaymentAmountOption[];
  /** A final payment that clears this balance may be below the usual minimum. */
  remainingBalance?: number;
  initialKey?: string;
  isSubmitting: boolean;
  isDirectOffer?: boolean;
  onClose: () => void;
  onSubmit: (option: PaymentAmountOption) => void;
}

export const RequestPaymentModal: React.FC<RequestPaymentModalProps> = ({
  currency,
  payerName,
  options,
  remainingBalance,
  initialKey,
  isSubmitting,
  isDirectOffer = false,
  onClose,
  onSubmit,
}) => {
  const [selectedKey, setSelectedKey] = useState(
    initialKey && options.some((o) => o.key === initialKey) ? initialKey : (options[0]?.key ?? ''),
  );
  const selected = options.find((o) => o.key === selectedKey);
  const settlesBalance =
    !!selected && remainingBalance != null && Math.abs(selected.amount - remainingBalance) < 0.005;
  const belowMinimum = !!selected && selected.amount < MIN_OFFER_PAYMENT_AMOUNT && !settlesBalance;

  return (
    <OfferModal
      title="Request payment"
      subtitle={`${payerName} gets a secure Stripe link to pay you.`}
      onClose={onClose}
    >
      <div className="space-y-2">
        <p className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider">
          How much are you requesting?
        </p>
        <PaymentOptionList
          options={options}
          selectedKey={selectedKey}
          onSelect={setSelectedKey}
          currency={currency}
          formatAmount={formatMoney}
        />
        {belowMinimum && (
          <p className="text-[11px] text-red-400">Payments must be at least $10.00.</p>
        )}
      </div>

      {selected && selected.amount > 0 && (
        <div className="rounded-2xl border border-[#22242a] bg-[#0c0d0f] p-4">
          <OfferFeeSummary
            currency={currency}
            gigAmount={selected.amount}
            isDirectOffer={isDirectOffer}
            perspective="both"
            viewerRole="payee"
          />
        </div>
      )}

      <div className="flex items-center gap-3 pt-1">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 rounded-full border border-[#2e3138] bg-[#1a1b1f] py-3 text-xs font-bold text-white hover:bg-[#24262c] transition cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => selected && onSubmit(selected)}
          disabled={!selected || belowMinimum || isSubmitting}
          className="flex-1 rounded-full bg-[#22c55e] py-3 text-xs font-bold text-black hover:bg-[#16a34a] transition cursor-pointer shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Requesting…' : 'Request Payment'}
        </button>
      </div>
    </OfferModal>
  );
};
